import React, { createContext, useContext, useState } from "react";
import { Select } from "antd";

const STORAGE_KEY = "cdx_lang";
const VALID_LANGS = ["en", "th", "zh"];
const getSavedLang = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return VALID_LANGS.includes(v) ? v : "en";
  } catch {
    return "en";
  }
};

const LanguageContext = createContext({ lang: "en", setLang: () => {} });

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(getSavedLang);
  const setLang = (v) => {
    try {
      localStorage.setItem(STORAGE_KEY, v);
    } catch {}
    setLangState(v);
  };
  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

export const UI_TRANSLATIONS = {
  en: {
    nav: {
      overview: "Home",
      screen: "Screen",
      library: "Media",
      playlist: "Playlist",
    },
    pageTitle: {
      overview: "Home",
      screen: (hasScreen, total) =>
        `My Screen${total > 2 ? ` (1 active, ${total - 2} locked)` : ` (${total})`}`,
      library: (count) => `Medias (${count} files)`,
      playlist: (count) => `Loop Playlist (${count} items)`,
    },
    upgradeTooltip: "Upgrade to Pro",
    upgradePro: "Pro",
    help: "Help",
    signOut: "Sign Out",
    proModal: {
      headline1: "Unlock the full power",
      headline2: "PushCodex",
      badge: "PRO PLAN",
      salesTitle: "Talk to our sales team",
      salesDesc:
        "Leave your details and our team will contact you with the best plan for your business",
      labelEmail: "Work email",
      placeholderEmail: "you@example.com",
      labelCompany: "Company name",
      placeholderCompany: "Your company name",
      labelScreens: "Number of screens",
      placeholderScreens: "e.g. 10, 50, 100+",
      labelPhone: "Phone number",
      placeholderPhone: "Your phone number",
      labelNeeds: "Tell us about your needs",
      placeholderNeeds: "What are you looking to achieve with PushCodex?",
      btnContact: "Contact Sales",
      btnSending: "Sending...",
      warnEmail: "Please enter your work email",
      successMsg: "Thanks! Our sales team will be in touch soon.",
      secureNote: "Your information is safe and secure.",
      features: [
        {
          title: "Unlimited screens & locations",
          desc: "Manage unlimited screens and locations across your organization",
        },
        {
          title: "Multi-zone custom layouts",
          desc: "Design advanced multi-zone layouts with complete creative flexibility",
        },
        {
          title: "Advanced scheduling & day-parting",
          desc: "Schedule content by day, time, and event with powerful day-parting rules",
        },
        {
          title: "Premium widgets & templates",
          desc: "Access premium widgets, templates, and integrations to engage your audience",
        },
        {
          title: "Team collaboration",
          desc: "Invite team members, set roles and permissions, and collaborate seamlessly",
        },
      ],
    },
    overview: {
      welcome: "Welcome to",
      welcomeSuffix: "Lite",
      welcomeEnd: "Workspace",
      subtitle:
        "Your simplified digital signage control center. Manage one screen, one loop — effortlessly.",
      displayCard: "My Screen",
      connected: "Connected",
      noPaired: "No screen paired",
      online: "Online",
      offline: "Offline",
      slotUsed: "1/1 slot used",
      tapToPair: "Tap to pair",
      libraryCard: "Media",
      mediaFiles: (n) => `${n} media files`,
      manageContent: "Upload & manage content",
      playlistCard: "Loop Playlist",
      itemsInLoop: (n) => `${n} items in loop`,
      reorderPublish: "Reorder & publish content",
      getStarted: "Get Started",
      manageDisplay: "Manage your display",
      connectDisplay: "Connect your display",
      manageDisplaySub: "Adjust settings, preview layout",
      connectDisplaySub: "Link a TV or monitor with a 6-digit code",
      uploadMore: "Upload more media",
      uploadFirst: "Upload your media",
      filesReady: (n) => `${n} files ready to use`,
      uploadSub: "Add images and videos to your library",
      editPublish: "Edit & publish content",
      createPlaylist: "Create your playlist & publish",
      itemsReady: (n) => `${n} items in your loop — publish anytime`,
      arrangeSub: "Arrange your content and push it to screen",
      completed: "✓ Completed",
      usage: "Usage",
      storage: "Storage",
      used: "used",
      limit: "Limit",
      proTip: "Pro Tip",
      proTipText:
        "Your loop playlist plays continuously on your display. Upload media to your library, then add items to the playlist in any order you like!",
      goToLibrary: "Go to Library →",
    },
    playlist: {
      title: "Loop Playlist",
      subtitle:
        "Arrange the order of content that plays continuously on your screen.",
      scheduleBtn: "Schedule",
      preview: "Preview",
      publish: "Publish",
      items: "items",
      totalLoop: "Total loop:",
      clearAll: "Clear All",
      emptyTitle: "Playlist is Empty",
      emptyText:
        "Browse your library or upload new files to start building your loop.",
      addContent: "Add Content",
      activeLoop: "Active Loop Sequence",
      dragToReorder: "Drag to reorder",
      addMore: "Add more from Library",
      howItWorks: "💡 How it works",
      howItWorksText:
        "Content plays from top to bottom in a continuous loop. Drag items to reorder. When you're ready, hit Publish to send the playlist to your screen. The playlist is independent — changing it here won't affect your display until you publish.",
      howItWorksTextTouch:
        "Content plays from top to bottom in a continuous loop. Use ▲▼ to reorder items. When you're ready, hit Publish to send the playlist to your screen. The playlist is independent — changing it here won't affect your display until you publish.",
      publishWord: "Publish",
      addSuccess: (name) => `"${name}" added to playlist.`,
      addFail: "Failed to add to playlist.",
      removeSuccess: "Removed from playlist.",
      removeFail: "Failed to remove from playlist.",
      deleteConfirmTitle: "Remove this media?",
      deleteConfirmContent: (name) =>
        `This will remove ${name ? `"${name}"` : "this item"} from the playlist.`,
      deleteConfirmOk: "Remove",
      deleteConfirmCancel: "Cancel",
      clearTitle: "Clear Entire Playlist?",
      clearContent:
        "This will remove all items from your loop. Your library files won't be affected.",
      clearOk: "Clear All",
      clearSuccess: "Playlist cleared.",
      clearFail: "Failed to clear playlist fully.",
      pairFirst: "Pair a screen first to publish your playlist.",
      pairBanner:
        "No screen paired — pair your display to start publishing content.",
      pairBannerBtn: "Go to Display →",
      addFirst: "Add content to your playlist first.",
      publishedSuccess: (n) => `Published ${n} items to your screen!`,
      publishFail: "Publish failed. Please try again.",
      proTitle: "Pro Feature: Advanced Scheduling",
      proContent:
        "Day-parting, calendar scheduling, and layered timelines are available on Pro. Lite plays all content in a simple continuous loop.",
      gotIt: "Got it",
    },
    screen: {
      title: "My Display",
      activeSubtitle: "Your single-screen setup is active.",
      inactiveSubtitle: "Connect a screen to get started.",
      maxScreens: "Max 2 Screen (Lite)",
      pairBtn: "Pair a Screen",
      addScreen2: "Add Screen 2",
      presetRatio: "Preset Ratio",
      customResolution: "Custom Resolution",
      manageContent: "Manage content for this screen",
      freeTier: "Free Tier • Single Screen",
      connection: "Connection",
      campaign: "Campaign",
      device: "Device",
      os: "Operating System",
      appVersion: "App Version",
      connectedStatus: (lastSeen) => `Connected • Last seen ${lastSeen}`,
      timezone: "Timezone",
      publishBtn: "Publish to Screen",
      refresh: "Refresh",
      unpair: "Unpair",
      screenPreview: "Screen Preview",
      zones: "Zones",
      fullScreenSlot: "Full Screen Slot",
      singleLayout: "Single full-screen layout",
      previewBtn: "Preview on Screen",
      editPlaylist: "Edit Playlist",
      noScreenTitle: "No Screen Connected",
      noScreenText:
        "Download the PushCodex TV app on your display device, then enter the 6-digit pairing code to connect.",
      pairScreen: "Pair Your Screen",
      otherScreens: (n) => `Other Screens (${n})`,
      upgradePro: "Upgrade to Pro to manage",
      locked: "Locked",
      linkTitle: "Link Your Screen",
      enterCodeHint: "Enter the 6-digit code shown on your TV",
      enterCodePlaceholder: "Enter Code...",
      connectNow: "✓ Connect Now",
      onlineStatus: "● Online",
      offlineStatus: "○ Offline",
      refreshSuccess: "Screen status refreshed.",
      unpairTitle: "Unpair Screen",
      unpairContent:
        "Are you sure you want to unpair this screen? The device will be disconnected from your workspace.",
      unpairOk: "Unpair",
      unpairCancel: "Cancel",
      unpairSuccess: "Screen unpaired successfully.",
      unpairFail: "Failed to unpair screen. Please try again.",
      pairSuccessTitle: "Screen Connected!",
      pairSuccessContent:
        "Your screen is now linked to your Lite workspace and ready to display content.",
      pairCodeError: "Please enter a 6-digit pairing code.",
      pairMissingData:
        "Unable to pair: missing account data. Please refresh and try again.",
      pairFail: "Pairing failed. Please check the code and try again.",
      publishAddFirst: "Add content to your playlist first before publishing.",
      publishNoScreen:
        "Screen data not available. Please refresh and try again.",
      publishNoCampaign:
        "No campaign is linked to this screen. Please assign a campaign before publishing.",
      publishSuccess: (n, name) => `Published ${n} items to ${name}!`,
      publishFail: "Publish failed. Please try again.",
      timezoneUpdated: (v) => `Timezone updated to ${v}`,
      timezoneLocal:
        "Timezone updated locally but could not be saved to device.",
      proZonesTitle: "Pro Feature: Multi-Zone Layouts",
      proZonesContent:
        "Split your screen into custom zones with different content. Available on Pro plan.",
      previewAddFirst: "Add content to your playlist first to preview.",
    },
    userProfile: {
      username: "Username",
      email: "Email",
      role: "Role",
      changePw: "Change Password",
      currentPw: "Current Password",
      currentPwPlaceholder: "Enter current password",
      currentPwRequired: "Please enter your current password",
      newPw: "New Password",
      newPwPlaceholder: "Enter new password",
      newPwRequired: "Please enter a new password",
      newPwMin: "Password must be at least 6 characters",
      confirmPw: "Confirm Password",
      confirmPwPlaceholder: "Re-enter new password",
      confirmPwRequired: "Please confirm your password",
      pwMismatch: "Passwords do not match",
      savePw: "Save New Password",
      pwChanged: "Password changed successfully",
      pwError: "Failed to change password. Please check your current password.",
      missingUser: "User information missing",
      editProfile: "Edit Profile",
      firstName: "First Name",
      firstNamePlaceholder: "Enter first name",
      lastName: "Last Name",
      lastNamePlaceholder: "Enter last name",
      company: "Company / Organization",
      companyPlaceholder: "Enter company name (optional)",
      emailEdit: "Email",
      emailPlaceholder: "Enter email address",
      emailInvalid: "Please enter a valid email address",
      saveProfile: "Save Profile",
      profileUpdated: "Profile updated successfully",
      profileError: "Failed to update profile. Please try again.",
    },
    topbar: {
      screens: "Screens",
      storage: "Storage",
    },
    library: {
      title: "Media Library",
      subtitle: "Upload and manage all your media files in one place.",
      dropzone: "Drop files here or click to upload",
      dropzoneHint: "JPG, PNG, GIF, MP4 \u2022 Max 1 GB per file",
      searchPlaceholder: "Search files...",
      files: "files",
      noMatch: "No files match your search",
      emptyTitle: "Library is empty",
      emptyHint: "Upload images or videos above to get started.",
      searchHint: "Try a different search term.",
      preview: "Preview",
      addToPlaylist: "Add to Playlist",
      delete: "Delete",
      addToLoop: "Add to Loop",
      rename: "Rename",
      renameTitle: "Rename File",
      renamePlaceholder: "Enter a new file name",
      renameOk: "Save",
      renameCancel: "Cancel",
      renameSuccess: "File renamed.",
      renameFail: "Failed to rename file.",
      renameEmpty: "File name cannot be empty.",
      deleteTitle: "Delete from Library?",
      deleteContent:
        "This will remove the file from your library. It will also be removed from your playlist if it's currently in use.",
      deleteOk: "Delete",
      close: "Close",
      type: "Type",
      size: "Size",
      duration: "Duration",
      uploaded: "Uploaded",
      uploadSuccess: (name) => `"${name}" uploaded successfully.`,
      uploadFail: (name) => `Upload failed for "${name}".`,
      addSuccess: (name) => `"${name}" added to playlist.`,
      addFail: "Failed to add to playlist.",
      deleteSuccess: "File deleted.",
      deleteFail: "Failed to delete file.",
      sizeExceeds: (name) => `"${name}" exceeds the maximum file size of 1 GB`,
      storageInsufficient: (remaining, requires) =>
        `Insufficient storage space \u2014 Remaining ${remaining}, File requires ${requires}`,
      invalidType: (name) =>
        `"${name}" is not a supported file type. Please upload JPG, PNG, GIF, or MP4 files.`,
    },
    guidedTour: {
      nextBtn: "Next →",
      prevBtn: "← Back",
      doneBtn: "Start Exploring! 🚀",
      progressText: "{{current}} of {{total}}",
      step1Title: "👋 Welcome to PushCodex Lite!",
      step1Desc:
        "Let's walk you through the essentials — from setting up your display to publishing your first content. It only takes a few minutes!",
      step2Title: "📍 Navigation Menu",
      step2Desc:
        "This is your main navigation. You have <strong>4 sections</strong>: Home, Display, Library, and Playlist. Each step of the workflow lives here.",
      step3Title: "📊 Status Bar",
      step3Desc:
        "You can always see which section you're in and your workspace name here.",
      step4aTitle: "📺 Step 1: Pair a Display",
      step4aDesc:
        "Your first step is to connect a screen. Click here to pair your TV or monitor using a simple 6-digit code.",
      step4bTitle: "📁 Step 2: Upload Content",
      step4bDesc:
        "Next, upload your images and videos to the content library. These are the media files that will play on your screen.",
      step4cTitle: "🎵 Step 3: Build Your Playlist",
      step4cDesc:
        "Finally, arrange your content into a playlist. Items play from top to bottom in a continuous loop on your display.",
      step4dTitle: "⚡ Quick Actions",
      step4dDesc:
        "This checklist tracks your setup progress. Completed steps get a green checkmark. Follow these 3 steps to go live!",
      step5Title: "📺 Let's Pair Your Screen",
      step5Desc:
        "Click <strong>Display</strong> in the sidebar to pair your first screen. This is where you connect a TV or monitor.",
      step6Title: "📁 Upload Your Content",
      step6Desc:
        "Click <strong>Library</strong> to upload images and videos. These files become available for your playlist.",
      step7Title: "🎵 Build & Publish",
      step7Desc:
        "Click <strong>Playlist</strong> to arrange your content order and hit <strong>Publish</strong> to push it to your screen!",
      step8Title: "🚀 Want More Power?",
      step8Desc:
        "The Lite version covers the basics. Upgrade to <strong>Pro</strong> for multi-screen, multi-zone layouts, advanced scheduling, and more!",
      step9Title: "🎉 You're All Set!",
      step9Desc:
        "Follow the 3-step flow: <strong>Pair Screen → Upload Content → Build Playlist</strong>. You'll be live in minutes! Click the <strong>?</strong> button anytime to replay this tour.",
    },
  },
  th: {
    nav: {
      overview: "หน้าแรก",
      screen: "จอแสดงผล",
      library: "คลังมีเดีย",
      playlist: "เพลย์ลิสต์",
    },
    pageTitle: {
      overview: "แดชบอร์ด",
      screen: (hasScreen, total) =>
        `จอแสดงผล${total > 2 ? ` (1 ใช้งาน, ${total - 2} ล็อก)` : ` (${total})`}`,
      library: (count) => `คลังมีเดีย (${count} ไฟล์)`,
      playlist: (count) => `เพลย์ลิสต์ (${count} รายการ)`,
    },
    upgradeTooltip: "อัปเกรดเป็น Pro",
    upgradePro: "อัปเกรด",
    help: "ช่วยเหลือ",
    signOut: "ออกจากระบบ",
    proModal: {
      headline1: "ปลดล็อกพลังเต็มรูปแบบของ",
      headline2: "PushCodex",
      badge: "แผน PRO",
      salesTitle: "คุยกับทีมขายของเรา",
      salesDesc:
        "ทิ้งข้อมูลของคุณไว้ แล้วทีมงานจะติดต่อกลับพร้อมแผนที่ดีที่สุดสำหรับธุรกิจของคุณ",
      labelEmail: "อีเมลที่ทำงาน",
      placeholderEmail: "you@example.com",
      labelCompany: "ชื่อบริษัท",
      placeholderCompany: "ชื่อบริษัทของคุณ",
      labelScreens: "จำนวนจอแสดงผล",
      placeholderScreens: "เช่น 10, 50, 100+",
      labelPhone: "เบอร์โทรศัพท์",
      placeholderPhone: "เบอร์โทรศัพท์ของคุณ",
      labelNeeds: "บอกเราเกี่ยวกับความต้องการของคุณ",
      placeholderNeeds: "คุณต้องการบรรลุอะไรกับ PushCodex?",
      btnContact: "ติดต่อทีมขาย",
      btnSending: "กำลังส่ง...",
      warnEmail: "กรุณากรอกอีเมลที่ทำงาน",
      successMsg: "ขอบคุณ! ทีมขายของเราจะติดต่อกลับในเร็วๆ นี้",
      secureNote: "ข้อมูลของคุณปลอดภัยและเป็นความลับ",
      features: [
        {
          title: "จอแสดงผลและสาขาไม่จำกัด",
          desc: "จัดการจอแสดงผลและสาขาได้ไม่จำกัดทั่วองค์กร",
        },
        {
          title: "เลย์เอาต์แบบหลายโซน",
          desc: "ออกแบบเลย์เอาต์หลายโซนขั้นสูงด้วยความยืดหยุ่นเต็มรูปแบบ",
        },
        {
          title: "กำหนดเวลาขั้นสูงและ Day-parting",
          desc: "กำหนดเนื้อหาตามวัน เวลา และอีเวนต์ด้วยกฎ Day-parting",
        },
        {
          title: "วิดเจ็ตและเทมเพลตพรีเมียม",
          desc: "เข้าถึงวิดเจ็ต เทมเพลต และการเชื่อมต่อพรีเมียม",
        },
        {
          title: "การทำงานร่วมกันเป็นทีม",
          desc: "เชิญสมาชิก กำหนดบทบาทและสิทธิ์ และทำงานร่วมกันได้อย่างราบรื่น",
        },
      ],
    },
    overview: {
      welcome: "ยินดีต้อนรับสู่",
      welcomeSuffix: "Lite",
      welcomeEnd: "Workspace",
      subtitle:
        "ศูนย์จัดการป้ายดิจิทัลของคุณ จัดการได้ง่าย เพียงหนึ่งจอ หนึ่งลูป",
      displayCard: "จอแสดงผล",
      connected: "เชื่อมต่อแล้ว",
      noPaired: "ยังไม่ได้เชื่อมต่อจอ",
      online: "ออนไลน์",
      offline: "ออฟไลน์",
      slotUsed: "ใช้งาน 1/1 ช่อง",
      tapToPair: "แตะเพื่อเชื่อมต่อ",
      libraryCard: "คลังมีเดีย",
      mediaFiles: (n) => `${n} ไฟล์มีเดีย`,
      manageContent: "อัปโหลดและจัดการเนื้อหา",
      playlistCard: "เพลย์ลิสต์",
      itemsInLoop: (n) => `${n} รายการในลูป`,
      reorderPublish: "เรียงลำดับและเผยแพร่เนื้อหา",
      getStarted: "เริ่มต้นใช้งาน",
      manageDisplay: "จัดการจอแสดงผล",
      connectDisplay: "เชื่อมต่อจอแสดงผล",
      manageDisplaySub: "ปรับการตั้งค่า ดูตัวอย่างเลย์เอาต์",
      connectDisplaySub: "เชื่อมต่อทีวีหรือจอโดยใช้รหัส 6 หลัก",
      uploadMore: "อัปโหลดไฟล์เพิ่มเติม",
      uploadFirst: "อัปโหลดไฟล์มีเดียของคุณ",
      filesReady: (n) => `${n} ไฟล์พร้อมใช้งาน`,
      uploadSub: "เพิ่มรูปภาพและวิดีโอลงในคลังมีเดีย",
      editPublish: "แก้ไขและเผยแพร่เนื้อหา",
      createPlaylist: "สร้างเพลย์ลิสต์และเผยแพร่",
      itemsReady: (n) => `${n} รายการในลูป — พร้อมเผยแพร่`,
      arrangeSub: "จัดเรียงเนื้อหาแล้วส่งไปยังจอ",
      completed: "✓ เสร็จสิ้น",
      usage: "การใช้งาน",
      storage: "พื้นที่จัดเก็บ",
      used: "ที่ใช้",
      limit: "ขีดจำกัด",
      proTip: "เคล็ดลับ Pro",
      proTipText:
        "เพลย์ลิสต์จะเล่นต่อเนื่องบนจอของคุณ อัปโหลดไฟล์มีเดียลงในคลัง แล้วเพิ่มรายการในเพลย์ลิสต์ตามลำดับที่ต้องการ",
      goToLibrary: "ไปที่คลังมีเดีย →",
    },
    playlist: {
      title: "เพลย์ลิสต์",
      subtitle: "จัดเรียงลำดับเนื้อหาที่เล่นต่อเนื่องบนจอแสดงผลของคุณ",
      scheduleBtn: "ตั้งเวลา",
      preview: "ดูตัวอย่าง",
      publish: "เผยแพร่",
      items: "รายการ",
      totalLoop: "รวมทั้งหมด:",
      clearAll: "ล้างทั้งหมด",
      emptyTitle: "เพลย์ลิสต์ว่างเปล่า",
      emptyText: "เรียกดูคลังมีเดียหรืออัปโหลดไฟล์ใหม่เพื่อเริ่มสร้างลูปของคุณ",
      addContent: "เพิ่มเนื้อหา",
      activeLoop: "ลำดับลูปที่ใช้งาน",
      dragToReorder: "ลากเพื่อเรียงลำดับใหม่",
      addMore: "เพิ่มจากคลังมีเดีย",
      howItWorks: "💡 วิธีการทำงาน",
      howItWorksText:
        "เนื้อหาเล่นจากบนลงล่างแบบวนซ้ำต่อเนื่อง ลากรายการเพื่อเรียงลำดับใหม่ เมื่อพร้อมแล้วกด เผยแพร่ เพื่อส่งเพลย์ลิสต์ไปยังจอ เพลย์ลิสต์จะไม่มีผลต่อจอจนกว่าคุณจะเผยแพร่",
      howItWorksTextTouch:
        "เนื้อหาเล่นจากบนลงล่างแบบวนซ้ำต่อเนื่อง ใช้ ▲▼ เพื่อเรียงลำดับใหม่ เมื่อพร้อมแล้วกด เผยแพร่ เพื่อส่งเพลย์ลิสต์ไปยังจอ เพลย์ลิสต์จะไม่มีผลต่อจอจนกว่าคุณจะเผยแพร่",
      publishWord: "เผยแพร่",
      addSuccess: (name) => `เพิ่ม "${name}" ในเพลย์ลิสต์แล้ว`,
      addFail: "ไม่สามารถเพิ่มในเพลย์ลิสต์ได้",
      removeSuccess: "นำออกจากเพลย์ลิสต์แล้ว",
      removeFail: "ไม่สามารถนำออกจากเพลย์ลิสต์ได้",
      deleteConfirmTitle: "เอาสื่อนี้ออกหรือไม่?",
      deleteConfirmContent: (name) =>
        `การดำเนินการนี้จะนำ ${name ? `"${name}"` : "รายการนี้"} ออกจากเพลย์ลิสต์`,
      deleteConfirmOk: "เอาออก",
      deleteConfirmCancel: "ยกเลิก",
      clearTitle: "ล้างเพลย์ลิสต์ทั้งหมด?",
      clearContent:
        "การดำเนินการนี้จะลบรายการทั้งหมดออกจากลูป แต่ไฟล์ในคลังจะไม่ถูกลบ",
      clearOk: "ล้างทั้งหมด",
      clearSuccess: "ล้างเพลย์ลิสต์แล้ว",
      clearFail: "ไม่สามารถล้างเพลย์ลิสต์ได้ทั้งหมด",
      pairFirst: "กรุณาเชื่อมต่อจอก่อนจึงจะเผยแพร่ได้",
      pairBanner:
        "ยังไม่ได้เชื่อมต่อจอ — เชื่อมต่อจอแสดงผลเพื่อเริ่มเผยแพร่เนื้อหา",
      pairBannerBtn: "ไปที่จอแสดงผล →",
      addFirst: "กรุณาเพิ่มเนื้อหาในเพลย์ลิสต์ก่อน",
      publishedSuccess: (n) => `เผยแพร่ ${n} รายการไปยังจอแล้ว!`,
      publishFail: "เผยแพร่ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
      proTitle: "ฟีเจอร์ Pro: การตั้งเวลาขั้นสูง",
      proContent:
        "การแบ่งช่วงเวลา การตั้งตารางปฏิทิน และไทม์ไลน์แบบหลายชั้นสามารถใช้งานได้บนแผน Pro",
      gotIt: "เข้าใจแล้ว",
    },
    screen: {
      title: "จอแสดงผลของฉัน",
      activeSubtitle: "การตั้งค่าจอของคุณพร้อมใช้งานแล้ว",
      inactiveSubtitle: "เชื่อมต่อจอเพื่อเริ่มต้นใช้งาน",
      maxScreens: "สูงสุด 2 จอ (Lite)",
      pairBtn: "เชื่อมต่อจอ",
      addScreen2: "เพิ่มจอที่ 2",
      presetRatio: "อัตราส่วนสำเร็จรูป",
      customResolution: "ความและเอียดกำหนดเอง",
      manageContent: "*จัดการเนื้อหาสำหรับจอนี้",
      freeTier: "แผนฟรี • จอเดี่ยว",
      connection: "การเชื่อมต่อ",
      campaign: "แคมเปญ",
      device: "อุปกรณ์",
      os: "ระบบปฏิบัติการ",
      appVersion: "เวอร์ชันแอป",
      connectedStatus: (lastSeen) => `เชื่อมต่อแล้ว • พบล่าสุด ${lastSeen}`,
      timezone: "เขตเวลา",
      publishBtn: "เผยแพร่ไปยังจอ",
      refresh: "รีเฟรช",
      unpair: "ยกเลิกการเชื่อมต่อ",
      screenPreview: "ตัวอย่างจอ",
      zones: "โซน",
      fullScreenSlot: "เต็มหน้าจอ",
      singleLayout: "เลย์เอาต์เต็มหน้าจอเดี่ยว",
      previewBtn: "ดูตัวอย่างบนจอ",
      editPlaylist: "แก้ไขเพลย์ลิสต์",
      noScreenTitle: "ยังไม่ได้เชื่อมต่อจอ",
      noScreenText:
        "ดาวน์โหลดแอป PushCodex TV บนอุปกรณ์แสดงผล จากนั้นกรอกรหัสจับคู่ 6 หลักเพื่อเชื่อมต่อ",
      pairScreen: "จับคู่จอของคุณ",
      otherScreens: (n) => `จออื่น ๆ (${n})`,
      upgradePro: "อัปเกรดเป็น Pro เพื่อจัดการ",
      locked: "ล็อก",
      linkTitle: "เชื่อมต่อจอของคุณ",
      enterCodeHint: "กรอกรหัส 6 หลักที่แสดงบนทีวีของคุณ",
      enterCodePlaceholder: "กรอกรหัส...",
      connectNow: "✓ เชื่อมต่อเลย",
      onlineStatus: "● ออนไลน์",
      offlineStatus: "○ ออฟไลน์",
      refreshSuccess: "รีเฟรชสถานะจอแล้ว",
      unpairTitle: "ยกเลิกการเชื่อมต่อจอ",
      unpairContent:
        "คุณแน่ใจหรือไม่ว่าต้องการยกเลิกการเชื่อมต่อจอนี้? อุปกรณ์จะถูกตัดการเชื่อมต่อจากเวิร์กสเปซของคุณ",
      unpairOk: "ยกเลิกการเชื่อมต่อ",
      unpairCancel: "ยกเลิก",
      unpairSuccess: "ยกเลิกการเชื่อมต่อจอสำเร็จ",
      unpairFail: "ไม่สามารถยกเลิกการเชื่อมต่อได้ กรุณาลองใหม่",
      pairSuccessTitle: "เชื่อมต่อจอสำเร็จ!",
      pairSuccessContent:
        "จอของคุณเชื่อมต่อกับ Lite Workspace แล้วและพร้อมแสดงเนื้อหา",
      pairCodeError: "กรุณากรอกรหัสจับคู่ 6 หลัก",
      pairMissingData:
        "ไม่สามารถจับคู่ได้: ข้อมูลบัญชีไม่ครบ กรุณารีเฟรชและลองใหม่",
      pairFail: "การจับคู่ล้มเหลว กรุณาตรวจสอบรหัสและลองใหม่",
      publishAddFirst: "กรุณาเพิ่มเนื้อหาในเพลย์ลิสต์ก่อนเผยแพร่",
      publishNoScreen: "ข้อมูลจอไม่พร้อมใช้งาน กรุณารีเฟรชและลองใหม่",
      publishNoCampaign:
        "ไม่มีแคมเปญเชื่อมต่อกับจอนี้ กรุณากำหนดแคมเปญก่อนเผยแพร่",
      publishSuccess: (n, name) => `เผยแพร่ ${n} รายการไปยัง ${name} แล้ว!`,
      publishFail: "เผยแพร่ไม่สำเร็จ กรุณาลองใหม่",
      timezoneUpdated: (v) => `อัปเดตเขตเวลาเป็น ${v} แล้ว`,
      timezoneLocal:
        "อัปเดตเขตเวลาในเครื่องแล้ว แต่ไม่สามารถบันทึกไปยังอุปกรณ์ได้",
      proZonesTitle: "ฟีเจอร์ Pro: เลย์เอาต์หลายโซน",
      proZonesContent:
        "แบ่งหน้าจอออกเป็นโซนต่าง ๆ พร้อมเนื้อหาที่แตกต่างกัน ใช้งานได้บนแผน Pro",
      previewAddFirst: "กรุณาเพิ่มเนื้อหาในเพลย์ลิสต์ก่อนเพื่อดูตัวอย่าง",
    },
    userProfile: {
      username: "ชื่อผู้ใช้",
      email: "อีเมล",
      role: "บทบาท",
      changePw: "เปลี่ยนรหัสผ่าน",
      currentPw: "รหัสผ่านปัจจุบัน",
      currentPwPlaceholder: "กรอกรหัสผ่านปัจจุบัน",
      currentPwRequired: "กรุณากรอกรหัสผ่านปัจจุบัน",
      newPw: "รหัสผ่านใหม่",
      newPwPlaceholder: "กรอกรหัสผ่านใหม่",
      newPwRequired: "กรุณากรอกรหัสผ่านใหม่",
      newPwMin: "รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร",
      confirmPw: "ยืนยันรหัสผ่าน",
      confirmPwPlaceholder: "กรอกรหัสผ่านใหม่อีกครั้ง",
      confirmPwRequired: "กรุณายืนยันรหัสผ่าน",
      pwMismatch: "รหัสผ่านไม่ตรงกัน",
      savePw: "บันทึกรหัสผ่านใหม่",
      pwChanged: "เปลี่ยนรหัสผ่านสำเร็จ",
      pwError: "ไม่สามารถเปลี่ยนรหัสผ่านได้ กรุณาตรวจสอบรหัสผ่านปัจจุบัน",
      missingUser: "ไม่พบข้อมูลผู้ใช้",
      editProfile: "แก้ไขโปรไฟล์",
      firstName: "ชื่อจริง",
      firstNamePlaceholder: "กรอกชื่อจริง",
      lastName: "นามสกุล",
      lastNamePlaceholder: "กรอกนามสกุล",
      company: "บริษัท / องค์กร",
      companyPlaceholder: "กรอกชื่อบริษัท (ถ้ามี)",
      emailEdit: "อีเมล",
      emailPlaceholder: "กรอกอีเมล",
      emailInvalid: "กรุณากรอกอีเมลที่ถูกต้อง",
      saveProfile: "บันทึกโปรไฟล์",
      profileUpdated: "อัปเดตโปรไฟล์สำเร็จ",
      profileError: "ไม่สามารถอัปเดตโปรไฟล์ได้ กรุณาลองใหม่",
    },
    topbar: {
      screens: "จอ",
      storage: "พื้นที่",
    },
    library: {
      title: "คลังมีเดีย",
      subtitle: "อัปโหลดและจัดการไฟล์มีเดียทั้งหมดของคุณในที่เดียว",
      dropzone: "วางไฟล์ที่นี่หรือคลิกเพื่ออัปโหลด",
      dropzoneHint: "JPG, PNG, GIF, MP4 \u2022 ขนาดสูงสุด 1 GB ต่อไฟล์",
      searchPlaceholder: "ค้นหาไฟล์...",
      files: "ไฟล์",
      noMatch: "ไม่พบไฟล์ที่ตรงกับคำค้นหา",
      emptyTitle: "คลังมีเดียว่างเปล่า",
      emptyHint: "อัปโหลดรูปภาพหรือวิดีโอด้านบนเพื่อเริ่มต้นใช้งาน",
      searchHint: "ลองใช้คำค้นหาอื่น",
      preview: "ดูตัวอย่าง",
      addToPlaylist: "เพิ่มในเพลย์ลิสต์",
      delete: "ลบ",
      addToLoop: "เพิ่มในลูป",
      rename: "เปลี่ยนชื่อ",
      renameTitle: "เปลี่ยนชื่อไฟล์",
      renamePlaceholder: "กรอกชื่อไฟล์ใหม่",
      renameOk: "บันทึก",
      renameCancel: "ยกเลิก",
      renameSuccess: "เปลี่ยนชื่อไฟล์แล้ว",
      renameFail: "ไม่สามารถเปลี่ยนชื่อไฟล์ได้",
      renameEmpty: "กรุณากรอกชื่อไฟล์",
      deleteTitle: "ลบออกจากคลังมีเดีย?",
      deleteContent:
        "ไฟล์จะถูกลบออกจากคลังมีเดียของคุณ และจะถูกนำออกจากเพลย์ลิสต์หากกำลังใช้งานอยู่",
      deleteOk: "ลบ",
      close: "ปิด",
      type: "ประเภท",
      size: "ขนาด",
      duration: "ความยาว",
      uploaded: "อัปโหลดเมื่อ",
      uploadSuccess: (name) => `อัปโหลด "${name}" สำเร็จ`,
      uploadFail: (name) => `อัปโหลด "${name}" ไม่สำเร็จ`,
      addSuccess: (name) => `เพิ่ม "${name}" ในเพลย์ลิสต์แล้ว`,
      addFail: "ไม่สามารถเพิ่มในเพลย์ลิสต์ได้",
      deleteSuccess: "ลบไฟล์แล้ว",
      deleteFail: "ไม่สามารถลบไฟล์ได้",
      sizeExceeds: (name) => `"${name}" มีขนาดเกิน 1 GB`,
      storageInsufficient: (remaining, requires) =>
        `พื้นที่เก็บข้อมูลไม่เพียงพอ \u2014 เหลือ ${remaining}, ไฟล์ต้องการ ${requires}`,
      invalidType: (name) =>
        `"${name}" ไม่ใช่ประเภทไฟล์ที่รองรับ กรุณาอัปโหลด JPG, PNG, GIF หรือ MP4`,
    },
    guidedTour: {
      nextBtn: "ถัดไป →",
      prevBtn: "← ย้อนกลับ",
      doneBtn: "เริ่มสำรวจ! 🚀",
      progressText: "{{current}} จาก {{total}}",
      step1Title: "👋 ยินดีต้อนรับสู่ PushCodex Lite!",
      step1Desc:
        "มาเรียนรู้พื้นฐานกัน — ตั้งแต่การเชื่อมต่อจอแสดงผลไปจนถึงการเผยแพร่เนื้อหาแรกของคุณ ใช้เวลาเพียงไม่กี่นาที!",
      step2Title: "📍 เมนูนำทาง",
      step2Desc:
        "นี่คือเมนูหลักของคุณ มี <strong>4 ส่วน</strong>: หน้าแรก, จอแสดงผล, คลังมีเดีย และเพลย์ลิสต์ แต่ละขั้นตอนของงานอยู่ที่นี่",
      step3Title: "📊 แถบสถานะ",
      step3Desc: "คุณสามารถดูได้ตลอดว่าอยู่ที่ส่วนใดและชื่อ Workspace ของคุณ",
      step4aTitle: "📺 ขั้นที่ 1: เชื่อมต่อจอแสดงผล",
      step4aDesc:
        "ขั้นตอนแรกคือเชื่อมต่อจอแสดงผล คลิกที่นี่เพื่อจับคู่ทีวีหรือมอนิเตอร์ด้วยรหัส 6 หลัก",
      step4bTitle: "📁 ขั้นที่ 2: อัปโหลดเนื้อหา",
      step4bDesc:
        "อัปโหลดรูปภาพและวิดีโอลงในคลังเนื้อหา ไฟล์เหล่านี้จะเล่นบนจอแสดงผลของคุณ",
      step4cTitle: "🎵 ขั้นที่ 3: สร้างเพลย์ลิสต์",
      step4cDesc:
        "จัดเรียงเนื้อหาในเพลย์ลิสต์ รายการจะเล่นจากบนลงล่างวนซ้ำต่อเนื่องบนจอแสดงผล",
      step4dTitle: "⚡ การดำเนินการด่วน",
      step4dDesc:
        "รายการตรวจสอบนี้ติดตามความคืบหน้าการตั้งค่าของคุณ ขั้นตอนที่เสร็จแล้วจะแสดงเครื่องหมายถูกสีเขียว ทำตาม 3 ขั้นตอนเพื่อเริ่มออกอากาศ!",
      step5Title: "📺 มาจับคู่จอแสดงผลกัน",
      step5Desc:
        "คลิก <strong>จอแสดงผล</strong> ในแถบด้านข้างเพื่อจับคู่จอแรกของคุณ นี่คือที่สำหรับเชื่อมต่อทีวีหรือมอนิเตอร์",
      step6Title: "📁 อัปโหลดเนื้อหา",
      step6Desc:
        "คลิก <strong>คลังมีเดีย</strong> เพื่ออัปโหลดรูปภาพและวิดีโอ ไฟล์เหล่านี้จะพร้อมใช้ในเพลย์ลิสต์",
      step7Title: "🎵 สร้างและเผยแพร่",
      step7Desc:
        "คลิก <strong>เพลย์ลิสต์</strong> เพื่อจัดลำดับเนื้อหาและกด <strong>เผยแพร่</strong> เพื่อส่งไปยังจอแสดงผล!",
      step8Title: "🚀 ต้องการความสามารถมากขึ้น?",
      step8Desc:
        "เวอร์ชัน Lite ครอบคลุมพื้นฐาน อัปเกรดเป็น <strong>Pro</strong> สำหรับหลายจอ เลย์เอาต์หลายโซน การกำหนดเวลาขั้นสูง และอื่นๆ!",
      step9Title: "🎉 คุณพร้อมแล้ว!",
      step9Desc:
        "ทำตามขั้นตอน 3 ขั้น: <strong>จับคู่จอ → อัปโหลดเนื้อหา → สร้างเพลย์ลิสต์</strong> คุณจะออกอากาศได้ในไม่กี่นาที! กดปุ่ม <strong>?</strong> เมื่อใดก็ได้เพื่อดูทัวร์อีกครั้ง",
    },
  },
  zh: {
    nav: {
      overview: "主页",
      screen: "显示屏",
      library: "素材库",
      playlist: "播放列表",
    },
    pageTitle: {
      overview: "仪表板",
      screen: (hasScreen, total) =>
        `显示屏${total > 2 ? ` (1 活跃, ${total - 2} 已锁)` : ` (${total})`}`,
      library: (count) => `素材库 (${count} 个文件)`,
      playlist: (count) => `循环列表 (${count} 项)`,
    },
    upgradeTooltip: "升级到 Pro",
    upgradePro: "升级",
    help: "帮助",
    signOut: "退出登录",
    proModal: {
      headline1: "解锁完整功能",
      headline2: "PushCodex",
      badge: "PRO 方案",
      salesTitle: "联系我们的销售团队",
      salesDesc: "留下您的联系方式，我们的团队将为您提供最适合的方案",
      labelEmail: "工作邮箱",
      placeholderEmail: "you@example.com",
      labelCompany: "公司名称",
      placeholderCompany: "您的公司名称",
      labelScreens: "屏幕数量",
      placeholderScreens: "例如 10, 50, 100+",
      labelPhone: "手机号码",
      placeholderPhone: "您的手机号码",
      labelNeeds: "告诉我们您的需求",
      placeholderNeeds: "您希望通过 PushCodex 实现什么？",
      btnContact: "联系销售",
      btnSending: "发送中...",
      warnEmail: "请输入工作邮箱",
      successMsg: "感谢！我们的销售团队将尽快与您联系。",
      secureNote: "您的信息安全且保密。",
      features: [
        { title: "无限屏幕与地点", desc: "跨组织管理无限数量的屏幕和地点" },
        {
          title: "多区域自定义布局",
          desc: "设计具有完整创意灵活性的高级多区域布局",
        },
        {
          title: "高级排程与分时段",
          desc: "按日期、时间和活动安排内容，支持强大的分时段规则",
        },
        {
          title: "高级小部件和模板",
          desc: "访问高级小部件、模板和集成，吸引您的受众",
        },
        { title: "团队协作", desc: "邀请团队成员、设置角色和权限，无缝协作" },
      ],
    },
    overview: {
      welcome: "欢迎使用",
      welcomeSuffix: "Lite",
      welcomeEnd: "工作台",
      subtitle: "您的数字标牌控制中心，轻松管理一块屏幕，一个循环。",
      displayCard: "我的显示屏",
      connected: "已连接",
      noPaired: "未配对屏幕",
      online: "在线",
      offline: "离线",
      slotUsed: "已使用 1/1 个插槽",
      tapToPair: "点击配对",
      libraryCard: "素材库",
      mediaFiles: (n) => `${n} 个媒体文件`,
      manageContent: "上传和管理内容",
      playlistCard: "循环播放列表",
      itemsInLoop: (n) => `循环中有 ${n} 项`,
      reorderPublish: "重新排序并发布内容",
      getStarted: "开始使用",
      manageDisplay: "管理显示屏",
      connectDisplay: "连接显示屏",
      manageDisplaySub: "调整设置，预览布局",
      connectDisplaySub: "使用 6 位配对码连接电视或显示器",
      uploadMore: "上传更多媒体",
      uploadFirst: "上传您的媒体",
      filesReady: (n) => `${n} 个文件已就绪`,
      uploadSub: "将图片和视频添加到素材库",
      editPublish: "编辑并发布内容",
      createPlaylist: "创建播放列表并发布",
      itemsReady: (n) => `循环中有 ${n} 项 — 随时可发布`,
      arrangeSub: "排列内容并推送到屏幕",
      completed: "✓ 已完成",
      usage: "使用情况",
      storage: "存储空间",
      used: "已使用",
      limit: "上限",
      proTip: "专业提示",
      proTipText:
        "循环播放列表会在您的显示屏上持续播放。将媒体上传到素材库，然后按任意顺序添加到播放列表中！",
      goToLibrary: "前往素材库 →",
    },
    playlist: {
      title: "循环播放列表",
      subtitle: "排列在屏幕上持续循环播放的内容顺序。",
      scheduleBtn: "定时",
      preview: "预览",
      publish: "发布",
      items: "项",
      totalLoop: "总时长：",
      clearAll: "清空全部",
      emptyTitle: "播放列表为空",
      emptyText: "浏览素材库或上传新文件以开始创建您的循环。",
      addContent: "添加内容",
      activeLoop: "当前循环序列",
      dragToReorder: "拖动以重新排序",
      addMore: "从素材库添加更多",
      howItWorks: "💡 工作原理",
      howItWorksText:
        "内容从上到下循环播放。拖动项目可重新排序。准备好后，点击发布将播放列表推送到屏幕。在发布之前，更改不会影响显示屏。",
      howItWorksTextTouch:
        "内容从上到下循环播放。使用 ▲▼ 可重新排序。准备好后，点击发布将播放列表推送到屏幕。在发布之前，更改不会影响显示屏。",
      publishWord: "发布",
      addSuccess: (name) => `"${name}" 已添加到播放列表。`,
      addFail: "添加到播放列表失败。",
      removeSuccess: "已从播放列表中移除。",
      removeFail: "从播放列表中移除失败。",
      deleteConfirmTitle: "移除此素材？",
      deleteConfirmContent: (name) =>
        `这将把 ${name ? `"${name}"` : "该项目"} 从播放列表中移除。`,
      deleteConfirmOk: "移除",
      deleteConfirmCancel: "取消",
      clearTitle: "清空整个播放列表？",
      clearContent: "这将删除循环中的所有项目。素材库中的文件不会受到影响。",
      clearOk: "清空全部",
      clearSuccess: "播放列表已清空。",
      clearFail: "无法完全清空播放列表。",
      pairFirst: "请先配对屏幕，然后再发布播放列表。",
      pairBanner: "未配对屏幕 — 请配对显示屏以开始发布内容。",
      pairBannerBtn: "前往显示屏 →",
      addFirst: "请先向播放列表添加内容。",
      publishedSuccess: (n) => `已将 ${n} 项发布到您的屏幕！`,
      publishFail: "发布失败，请重试。",
      proTitle: "Pro 功能：高级排程",
      proContent:
        "分时段播放、日历排程和多层时间轴可在 Pro 计划中使用。Lite 版以简单循环的方式播放所有内容。",
      gotIt: "明白了",
    },
    screen: {
      title: "我的显示屏",
      activeSubtitle: "您的单屏设置已激活。",
      inactiveSubtitle: "连接屏幕即可开始使用。",
      maxScreens: "最多 2 块屏幕 (Lite)",
      pairBtn: "配对屏幕",
      addScreen2: "添加第二块屏幕",
      presetRatio: "预设比例",
      customResolution: "自定义分辨率",
      manageContent: "管理此屏幕的内容",
      freeTier: "免费版 • 单屏",
      connection: "连接状态",
      campaign: "活动",
      device: "设备",
      os: "操作系统",
      appVersion: "应用版本",
      connectedStatus: (lastSeen) => `已连接 • 最后在线 ${lastSeen}`,
      timezone: "时区",
      publishBtn: "发布到屏幕",
      refresh: "刷新",
      unpair: "取消配对",
      screenPreview: "屏幕预览",
      zones: "分区",
      fullScreenSlot: "全屏插槽",
      singleLayout: "单一全屏布局",
      previewBtn: "在屏幕上预览",
      editPlaylist: "编辑播放列表",
      noScreenTitle: "未连接屏幕",
      noScreenText:
        "在显示设备上下载 PushCodex TV 应用，然后输入 6 位配对码进行连接。",
      pairScreen: "配对您的屏幕",
      otherScreens: (n) => `其他屏幕 (${n})`,
      upgradePro: "升级到 Pro 以管理",
      locked: "已锁定",
      linkTitle: "连接您的屏幕",
      enterCodeHint: "输入显示在电视上的 6 位码",
      enterCodePlaceholder: "输入代码...",
      connectNow: "✓ 立即连接",
      onlineStatus: "● 在线",
      offlineStatus: "○ 离线",
      refreshSuccess: "屏幕状态已刷新。",
      unpairTitle: "取消配对屏幕",
      unpairContent: "确定要取消配对此屏幕吗？设备将从您的工作台断开连接。",
      unpairOk: "取消配对",
      unpairCancel: "取消",
      unpairSuccess: "屏幕取消配对成功。",
      unpairFail: "取消配对失败，请重试。",
      pairSuccessTitle: "屏幕已连接！",
      pairSuccessContent: "您的屏幕已连接到 Lite 工作台，随时可以显示内容。",
      pairCodeError: "请输入 6 位配对码。",
      pairMissingData: "无法配对：账户数据缺失，请刷新后重试。",
      pairFail: "配对失败，请检查代码后重试。",
      publishAddFirst: "请先向播放列表添加内容，然后再发布。",
      publishNoScreen: "屏幕数据不可用，请刷新后重试。",
      publishNoCampaign: "此屏幕未关联活动，请在发布前分配活动。",
      publishSuccess: (n, name) => `已将 ${n} 项发布到 ${name}！`,
      publishFail: "发布失败，请重试。",
      timezoneUpdated: (v) => `时区已更新为 ${v}`,
      timezoneLocal: "时区已在本地更新，但无法保存到设备。",
      proZonesTitle: "Pro 功能：多区域布局",
      proZonesContent:
        "将屏幕拆分为具有不同内容的自定义区域。在 Pro 计划中可用。",
      previewAddFirst: "请先向播放列表添加内容以进行预览。",
    },
    userProfile: {
      username: "用户名",
      email: "电子邮件",
      role: "角色",
      changePw: "修改密码",
      currentPw: "当前密码",
      currentPwPlaceholder: "请输入当前密码",
      currentPwRequired: "请输入当前密码",
      newPw: "新密码",
      newPwPlaceholder: "请输入新密码",
      newPwRequired: "请输入新密码",
      newPwMin: "密码至少需要6个字符",
      confirmPw: "确认密码",
      confirmPwPlaceholder: "请再次输入新密码",
      confirmPwRequired: "请确认密码",
      pwMismatch: "两次输入的密码不一致",
      savePw: "保存新密码",
      pwChanged: "密码修改成功",
      pwError: "修改密码失败，请检查当前密码是否正确",
      missingUser: "找不到用户信息",
      editProfile: "编辑资料",
      firstName: "名",
      firstNamePlaceholder: "请输入名字",
      lastName: "姓",
      lastNamePlaceholder: "请输入姓氏",
      company: "公司 / 组织",
      companyPlaceholder: "请输入公司名称（可选）",
      emailEdit: "电子邮件",
      emailPlaceholder: "请输入电子邮件地址",
      emailInvalid: "请输入有效的电子邮件地址",
      saveProfile: "保存资料",
      profileUpdated: "资料更新成功",
      profileError: "资料更新失败，请重试",
    },
    topbar: {
      screens: "屏幕",
      storage: "存储",
    },
    library: {
      title: "素材库",
      subtitle: "在一个地方上传和管理您的所有媒体文件。",
      dropzone: "将文件拖放于此或点击上传",
      dropzoneHint: "JPG, PNG, GIF, MP4 \u2022 每个文件最大 1 GB",
      searchPlaceholder: "搜索文件...",
      files: "个文件",
      noMatch: "没有符合搜索条件的文件",
      emptyTitle: "素材库为空",
      emptyHint: "请在上方上传图片或视频以开始使用。",
      searchHint: "请尝试其他搜索词。",
      preview: "预览",
      addToPlaylist: "添加到播放列表",
      delete: "删除",
      addToLoop: "添加到循环",
      rename: "重命名",
      renameTitle: "重命名文件",
      renamePlaceholder: "请输入新的文件名",
      renameOk: "保存",
      renameCancel: "取消",
      renameSuccess: "文件已重命名。",
      renameFail: "重命名文件失败。",
      renameEmpty: "文件名不能为空。",
      deleteTitle: "从素材库中删除？",
      deleteContent:
        "此操作将从您的素材库中删除该文件，如果正在使用，也会从播放列表中移除。",
      deleteOk: "删除",
      close: "关闭",
      type: "类型",
      size: "大小",
      duration: "时长",
      uploaded: "上传于",
      uploadSuccess: (name) => `"${name}" 上传成功。`,
      uploadFail: (name) => `"${name}" 上传失败。`,
      addSuccess: (name) => `"${name}" 已添加到播放列表。`,
      addFail: "添加到播放列表失败。",
      deleteSuccess: "文件已删除。",
      deleteFail: "删除文件失败。",
      sizeExceeds: (name) => `"${name}" 超过最大文件大小 1 GB`,
      storageInsufficient: (remaining, requires) =>
        `存储空间不足 \u2014 剩余 ${remaining}，文件需要 ${requires}`,
      invalidType: (name) =>
        `"${name}" 不是支持的文件类型，请上传 JPG、PNG、GIF 或 MP4 文件。`,
    },
    guidedTour: {
      nextBtn: "下一步 →",
      prevBtn: "← 返回",
      doneBtn: "开始探索！🚀",
      progressText: "{{current}} / {{total}}",
      step1Title: "👋 欢迎使用 PushCodex Lite！",
      step1Desc:
        "让我们带您了解基本操作 — 从设置显示屏到发布第一个内容，只需几分钟！",
      step2Title: "📍 导航菜单",
      step2Desc:
        "这是您的主导航栏，共有 <strong>4 个板块</strong>：主页、显示屏、素材库和播放列表。工作流程的每个步骤都在这里。",
      step3Title: "📊 状态栏",
      step3Desc: "您可以随时查看当前所在板块以及工作台名称。",
      step4aTitle: "📺 第一步：配对显示屏",
      step4aDesc: "首先连接显示屏，点击此处使用 6 位数字代码配对电视或显示器。",
      step4bTitle: "📁 第二步：上传内容",
      step4bDesc: "将图片和视频上传到素材库，这些文件将在您的显示屏上播放。",
      step4cTitle: "🎵 第三步：创建播放列表",
      step4cDesc: "将内容排列成播放列表，内容将从上到下循环播放。",
      step4dTitle: "⚡ 快捷操作",
      step4dDesc:
        "此清单追踪您的设置进度，已完成的步骤将显示绿色对勾。按照这 3 个步骤即可上线播放！",
      step5Title: "📺 配对您的显示屏",
      step5Desc:
        "点击侧边栏中的 <strong>显示屏</strong> 配对您的第一块屏幕，在这里连接电视或显示器。",
      step6Title: "📁 上传您的内容",
      step6Desc:
        "点击 <strong>素材库</strong> 上传图片和视频，这些文件将在播放列表中使用。",
      step7Title: "🎵 创建并发布",
      step7Desc:
        "点击 <strong>播放列表</strong> 排列内容顺序，然后点击 <strong>发布</strong> 推送到您的显示屏！",
      step8Title: "🚀 想要更多功能？",
      step8Desc:
        "Lite 版本涵盖基础功能，升级到 <strong>Pro</strong> 可获得多屏管理、多区域布局、高级排程等更多功能！",
      step9Title: "🎉 一切就绪！",
      step9Desc:
        "按照 3 步流程操作：<strong>配对显示屏 → 上传内容 → 创建播放列表</strong>。几分钟内即可上线！随时点击 <strong>?</strong> 按钮重播此引导。",
    },
  },
};

const LANGS = [
  { value: "en", code: "us", label: "English" },
  { value: "th", code: "th", label: "ภาษาไทย" },
  { value: "zh", code: "cn", label: "中文" },
];

const FlagImg = ({ code, size = 20 }) => (
  <img
    src={`https://flagcdn.com/16x12/${code}.png`}
    srcSet={`https://flagcdn.com/16x12/${code}.png 2x`}
    width={size}
    height={Math.round(size * 0.667)}
    alt={code}
    style={{ borderRadius: "2px", objectFit: "cover", display: "block" }}
  />
);

export const LangToggle = ({ compact = false }) => {
  const { lang, setLang } = useLanguage();
  const current = LANGS.find((o) => o.value === lang) || LANGS[0];
  return (
    <Select
      value={lang}
      onChange={setLang}
      size="small"
      dropdownMatchSelectWidth={false}
      optionLabelProp="label"
      className="lang-toggle-select"
      style={{ minWidth: compact ? 52 : 130 }}
    >
      {LANGS.map((o) => (
        <Select.Option
          key={o.value}
          value={o.value}
          label={
            compact ? (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FlagImg code={o.code} size={18} />
              </span>
            ) : (
              <span
                style={{ display: "flex", alignItems: "center", gap: "7px" }}
              >
                <FlagImg code={o.code} size={18} />
                <span style={{ fontSize: "12px", fontWeight: 600 }}>
                  {o.label}
                </span>
              </span>
            )
          }
        >
          <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FlagImg code={o.code} size={18} />
            <span style={{ fontSize: "13px" }}>{o.label}</span>
          </span>
        </Select.Option>
      ))}
    </Select>
  );
};
